import * as React from 'react'

import {
  LogInIcon,
  MailIcon,
  MapIcon,
  NavigationIcon,
  PhoneIcon,
} from 'lucide-react'
import { inject, observer } from 'mobx-react'

import { LocationType } from '~/client/graph'
import { DeliveryIcon } from '~/client/src/shared/components/DeliveryIcon/DeliveryIcon'
import DeliveryStatusText from '~/client/src/shared/components/DeliveryStatusText/DeliveryStatusText'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CalendarEvent from '~/client/src/shared/models/CalendarEvent'
import Delivery from '~/client/src/shared/models/Delivery'
import { SharedInfo } from '~/client/src/shared/models/SharedInfo'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import {
  INTERPUNCT,
  NOT_PROVIDED,
} from '~/client/src/shared/utils/usefulStrings'

interface IProps {
  event: CalendarEvent

  setIgnoreTooltip?(isIgnored: boolean): void

  state?: InitialState
  companiesStore?: CompaniesStore
  locationAttributesStore?: LocationAttributesStore
}

@inject('state', 'companiesStore', 'locationAttributesStore')
@observer
export default class DeliveryMultiDayEventLabel extends React.Component<IProps> {
  public render() {
    const { event } = this.props

    if (event.isNew) {
      return Localization.translator.newDelivery
    }

    const { companiesStore } = this.props

    const companyName = companiesStore.getCompanyNameById(
      this.delivery.company,
      null,
    )

    const label = (
      <div className="row">
        <DeliveryIcon
          className="row event-icon no-grow mr4"
          isFromCD={this.delivery.isFromConcreteDirect}
          color={event.styles.color}
        />
        {event.isShared ? (
          <>
            <span className="text-bold no-grow">
              {event.sharedInfo.companyName}
            </span>
            <span className="px4 no-grow">{INTERPUNCT}</span>
          </>
        ) : (
          <>
            <span className="text-bold no-grow">{companyName}</span>
            <span className="px4 no-grow">{INTERPUNCT}</span>
          </>
        )}

        <span className="text light no-grow">
          {!event.isShared
            ? this.delivery.codeToDisplay(companiesStore)
            : Localization.translator.shared}
        </span>
        <span className="px4 no-grow">{INTERPUNCT}</span>
        <DeliveryStatusText
          className="relative text bold capitalize no-grow"
          delivery={this.delivery}
        />

        {this.locationName && (
          <>
            <span className="px4 no-grow">{INTERPUNCT}</span>
            {this.locationName}
          </>
        )}
      </div>
    )

    if (!event.isShared) {
      return label
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger nativeButton={false} render={label} />
        {this.renderMenu()}
      </DropdownMenu>
    )
  }

  private renderMenu() {
    const { sharedInfo } = this.props.event

    return (
      <DropdownMenuContent
        side="bottom"
        align="center"
        className="tw:w-auto"
        onMouseEnter={this.setIgnoreTooltip}
        onMouseLeave={this.setToNotIgnoreTooltip}
      >
        <DropdownMenuItem onClick={this.handleProjectClick}>
          <LogInIcon />
          {Localization.translator.goToTheProject}
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={!sharedInfo?.locationsFrom?.length}>
            <NavigationIcon />
            {Localization.translator.locationsFrom}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {sharedInfo?.locationsFrom?.map((lf, idx) => (
              <DropdownMenuItem
                key={lf.locationName + idx}
                className="tw:max-w-[300px]"
                onClick={e => this.handleLocationClick(e, lf.locationUrl)}
              >
                <MapIcon />
                <span className="tw:truncate">
                  {Localization.translator.goToLocation(lf.locationName)}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={!sharedInfo?.locationsTo?.length}>
            <NavigationIcon />
            {Localization.translator.locationTo}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {sharedInfo?.locationsTo?.map((lt, idx) => (
              <DropdownMenuItem
                key={lt.locationName + idx}
                className="tw:max-w-[300px]"
                onClick={e => this.handleLocationClick(e, lt.locationUrl)}
              >
                <MapIcon />
                <span className="tw:truncate">
                  {Localization.translator.goToLocation(lt.locationName)}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuItem
          disabled={!this.isPhoneValid(sharedInfo?.responsible?.phoneNumber)}
          onClick={e => this.handleCallResponsible(e, sharedInfo)}
        >
          <PhoneIcon />
          {Localization.translator.callTo(sharedInfo?.responsible?.firstName)}
        </DropdownMenuItem>

        <DropdownMenuItem
          disabled={!sharedInfo?.responsible?.email}
          onClick={e => this.handleMailResponsible(e, sharedInfo)}
        >
          <MailIcon />
          {Localization.translator.mailTo(sharedInfo?.responsible?.firstName)}
        </DropdownMenuItem>
      </DropdownMenuContent>
    )
  }

  private handleLocationClick = (e, locationUrl: string) => {
    window.open(locationUrl, '_blank')
    e.stopPropagation()
  }

  private handleCallResponsible = (e, sharedInfo: SharedInfo) => {
    if (sharedInfo?.responsible?.phoneNumber) {
      window.open(`tel:${sharedInfo?.responsible.phoneNumber}`, '_blank')
    }
    e.stopPropagation()
  }

  private handleMailResponsible = (e, sharedInfo: SharedInfo) => {
    if (sharedInfo?.responsible?.email) {
      window.open(`mailto:${sharedInfo?.responsible.email}`, '_blank')
    }
    e.stopPropagation()
  }

  private handleProjectClick = (e: React.MouseEvent) => {
    const { projectUrl } = this.props.event.sharedInfo
    if (projectUrl) {
      window.open(projectUrl, '_blank')
    }
    e.stopPropagation()
  }

  private isPhoneValid(phone?: string) {
    return phone && phone !== NOT_PROVIDED
  }

  private get delivery(): Delivery {
    return this.props.event.data
  }

  private get locationName(): string {
    const { state, locationAttributesStore } = this.props

    switch (state.delivery.configurations.cardColoringLocationType) {
      case LocationType.Area:
        return locationAttributesStore.areasStore.getInstanceById(
          this.delivery.area,
        )?.name

      case LocationType.Building:
        return locationAttributesStore.buildingsStore.getInstanceById(
          this.delivery.building,
        )?.name

      case LocationType.Gate:
        return locationAttributesStore.gatesStore.getInstanceById(
          this.delivery.gate,
        )?.name

      case LocationType.Level:
        return locationAttributesStore.levelsStore.getInstanceById(
          this.delivery.level,
        )?.name

      case LocationType.Route:
        return locationAttributesStore.routesStore.getInstanceById(
          this.delivery.route,
        )?.name

      case LocationType.OffloadingEquipment:
        return locationAttributesStore.offloadingEquipmentsStore.getInstanceById(
          this.delivery.offloadingEquipmentIds?.[0],
        )?.name

      case LocationType.Zone:
      default:
        return locationAttributesStore.zonesStore.getInstanceById(
          this.delivery.zone,
        )?.name
      case LocationType.Staging:
        return locationAttributesStore.stagingsStore.getInstanceById(
          this.delivery.staging,
        )?.name

      case LocationType.InteriorDoor:
        return locationAttributesStore.interiorDoorsStore.getInstanceById(
          this.delivery.interiorDoor,
        )?.name

      case LocationType.InteriorPath:
        return locationAttributesStore.interiorPathsStore.getInstanceById(
          this.delivery.interiorPath,
        )?.name
    }
  }

  private setIgnoreTooltip = () => {
    this.props.setIgnoreTooltip?.(true)
  }

  private setToNotIgnoreTooltip = () => {
    this.props.setIgnoreTooltip?.(false)
  }
}
